/**
 * The cad-import (Rust/WASM) result, typed, plus the conversion of an import
 * into editor shapes: layer filter, unit scaling or fitting into the
 * footprint, centering on the origin, and collinear-point cleanup.
 */

import type { ObjectShape, Vec2 } from 'shared-types';
import { newShapeId, roundCoord } from '../document/specOps';

export type ImportedShape =
  | { kind: 'polyline'; layer: string; points: Vec2[]; closed: boolean }
  | { kind: 'circle'; layer: string; center: Vec2; radius: number }
  | { kind: 'arc'; layer: string; center: Vec2; radius: number; startAngle: number; endAngle: number };

export interface ImportResult {
  format: 'svg' | 'dxf' | 'dwg';
  metersPerUnit: number | null;
  unitName: string | null;
  layers: Array<{ name: string; shapeCount: number }>;
  shapes: ImportedShape[];
  bounds: [number, number, number, number] | null;
  warnings: string[];
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isVec = (v: unknown): v is Vec2 => Array.isArray(v) && v.length === 2 && isNum(v[0]) && isNum(v[1]);

function parseShape(v: unknown): ImportedShape | null {
  if (!isRecord(v) || typeof v['layer'] !== 'string') return null;
  const layer = v['layer'];
  switch (v['kind']) {
    case 'polyline':
      return Array.isArray(v['points']) && v['points'].every(isVec) ? { kind: 'polyline', layer, points: v['points'], closed: v['closed'] === true } : null;
    case 'circle':
      return isVec(v['center']) && isNum(v['radius']) ? { kind: 'circle', layer, center: v['center'], radius: v['radius'] } : null;
    case 'arc':
      return isVec(v['center']) && isNum(v['radius']) && isNum(v['startAngle']) && isNum(v['endAngle'])
        ? { kind: 'arc', layer, center: v['center'], radius: v['radius'], startAngle: v['startAngle'], endAngle: v['endAngle'] }
        : null;
    default:
      return null;
  }
}

/** Validate the worker's JSON (it crosses a trust boundary: the file is user data). */
export function parseImportResult(json: string): ImportResult | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isRecord(raw) || !['svg', 'dxf', 'dwg'].includes(String(raw['format']))) return null;
  const format = raw['format'] === 'dxf' ? 'dxf' : raw['format'] === 'dwg' ? 'dwg' : 'svg';
  const shapes = Array.isArray(raw['shapes']) ? raw['shapes'].map(parseShape).filter((s): s is ImportedShape => s !== null) : [];
  const layers = Array.isArray(raw['layers'])
    ? raw['layers'].filter(isRecord).map((l) => ({ name: String(l['name'] ?? ''), shapeCount: isNum(l['shapeCount']) ? l['shapeCount'] : 0 }))
    : [];
  const b = raw['bounds'];
  return {
    format,
    metersPerUnit: isNum(raw['metersPerUnit']) ? raw['metersPerUnit'] : null,
    unitName: typeof raw['unitName'] === 'string' ? raw['unitName'] : null,
    layers,
    shapes,
    bounds: Array.isArray(b) && b.length === 4 && b.every(isNum) ? [b[0], b[1], b[2], b[3]] : null,
    warnings: Array.isArray(raw['warnings']) ? raw['warnings'].map(String) : [],
  };
}

export type ImportPlacement = { mode: 'fit'; width: number; depth: number } | { mode: 'real'; metersPerUnit: number };

export interface ConvertedImport {
  shapes: ObjectShape[];
  /** Size of the imported drawing in meters (after scaling). */
  size: { width: number; depth: number };
}

const COLLINEAR_EPS = 1e-5;

/** Drop points that lie on the line through their neighbours (tessellated straight runs, duplicates). */
export function simplifyPolyline(points: readonly Vec2[], closed: boolean): Vec2[] {
  if (points.length <= 2) return [...points];
  const out: Vec2[] = [];
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const prev = out.length > 0 ? out[out.length - 1] : closed ? points[n - 1] : null;
    const next = i + 1 < n ? points[i + 1] : closed ? points[0] : null;
    const p = points[i];
    if (prev && Math.hypot(p[0] - prev[0], p[1] - prev[1]) < COLLINEAR_EPS) continue;
    if (prev && next) {
      const cross = (p[0] - prev[0]) * (next[1] - prev[1]) - (p[1] - prev[1]) * (next[0] - prev[0]);
      const len = Math.hypot(next[0] - prev[0], next[1] - prev[1]);
      if (len > 0 && Math.abs(cross) / len < COLLINEAR_EPS) continue;
    }
    out.push(p);
  }
  return out.length >= 2 ? out : [...points.slice(0, 2)];
}

/**
 * The selected layers' shapes in spec coordinates: scaled (real units or
 * fitted into the footprint, keeping proportions) and centered on the origin.
 */
export function convertImport(result: ImportResult, layers: ReadonlySet<string>, placement: ImportPlacement): ConvertedImport {
  const selected = result.shapes.filter((s) => layers.has(s.layer));
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const extend = (x: number, y: number) => {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };
  for (const s of selected) {
    if (s.kind === 'polyline') s.points.forEach((p) => extend(p[0], p[1]));
    else {
      extend(s.center[0] - s.radius, s.center[1] - s.radius);
      extend(s.center[0] + s.radius, s.center[1] + s.radius);
    }
  }
  if (!Number.isFinite(minX)) return { shapes: [], size: { width: 0, depth: 0 } };
  const w = maxX - minX;
  const h = maxY - minY;
  const scale = placement.mode === 'real' ? placement.metersPerUnit : Math.min(w > 0 ? placement.width / w : Infinity, h > 0 ? placement.depth / h : Infinity);
  const k = Number.isFinite(scale) && scale > 0 ? scale : 1;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const map = (p: Vec2): Vec2 => [roundCoord((p[0] - cx) * k), roundCoord((p[1] - cy) * k)];

  const shapes: ObjectShape[] = [];
  for (const s of selected) {
    const id = newShapeId();
    if (s.kind === 'polyline') {
      const points = simplifyPolyline(s.points.map(map), s.closed);
      if (points.length >= 2) shapes.push({ id, kind: 'polyline', points, closed: s.closed && points.length > 2 });
    } else if (s.kind === 'circle') {
      shapes.push({ id, kind: 'circle', center: map(s.center), radius: roundCoord(s.radius * k) });
    } else {
      shapes.push({ id, kind: 'arc', center: map(s.center), radius: roundCoord(s.radius * k), startAngle: s.startAngle, endAngle: s.endAngle });
    }
  }
  return { shapes, size: { width: roundCoord(w * k), depth: roundCoord(h * k) } };
}
